import React, { ReactNode } from 'react'
import { HTMLMessageDescriptor } from '@web/walter-lib/react-intl'
import { Button, FormattedHTMLMessage } from '@web/walter-ui-kit'
import TxNotificationIcon from '../TxNotificationIcon'
import styles from './style.module.css'
import { ProcessedNotificationData } from './types'
import { lang } from './lang'
import { Routes as AppRoutes } from '~constants/routes'

interface Props {
    /**
     * Notification title
     */
    title: HTMLMessageDescriptor
    /**
     * Notification content text
     */
    content?: HTMLMessageDescriptor
    /**
     * Custom icon
     */
    icon?: ReactNode
}

interface ErrorProps extends Props {
    /**
     * Error message
     */
    message?: string | HTMLMessageDescriptor
    /**
     * Button text
     */
    buttonText?: HTMLMessageDescriptor
    /**
     * ButtonLink
     */
    buttonLink?: string
    dataPointer?: string
    errorId?: string | number
}

export const getPendingNotification = ({ title, content, icon }: Props): ProcessedNotificationData => ({
    icon: icon || <TxNotificationIcon status="PENDING" />,
    title: <FormattedHTMLMessage {...title} />,
    content: content ? <FormattedHTMLMessage {...content} /> : undefined,
    buttonProps: {
        size: 's',
        role: 'link',
        to: AppRoutes.history,
        fullWidth: true,
        text: <FormattedHTMLMessage {...lang.pendingButton} />,
    },
    status: 'warning',
})

export const getErrorNotification = ({
    title,
    content,
    message,
    icon,
    buttonLink,
    buttonText,
    dataPointer,
    errorId,
}: ErrorProps): ProcessedNotificationData => ({
    icon: icon || <TxNotificationIcon status="FAILED" />,
    title: <FormattedHTMLMessage {...title} />,
    content: (
        <>
            {content && <FormattedHTMLMessage {...content} />}
            {message && (
                <div className={styles.errorMessage}>
                    {typeof message === 'string' ? (
                        <FormattedHTMLMessage
                            dataPointer={`error_id_${errorId || message}`}
                            defaultMessage={message}
                            id={`error_id_${errorId || message}`}
                        />
                    ) : (
                        <FormattedHTMLMessage {...message} />
                    )}
                </div>
            )}
            {buttonText && buttonLink && (
                <div className={styles.button}>
                    <Button dataPointer="notification_button" size="s" role="link" to={buttonLink} fullWidth>
                        <FormattedHTMLMessage {...buttonText} />
                    </Button>
                </div>
            )}
        </>
    ),
    status: 'error',
    dataPointer,
})
